import React from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import i18n from "i18next"; // Added to sync with cookie locale

// Custom SVG Marker Icon
const CustomMarker = new L.DivIcon({
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-map-pin">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M18.364 4.636a9 9 0 0 1 .203 12.519l-.203 .21l-4.243 4.242a3 3 0 0 1 -4.097 .135l-.144 -.135l-4.244 -4.243a9 9 0 0 1 12.728 -12.728zm-6.364 3.364a3 3 0 1 0 0 6a3 3 0 0 0 0 -6z" />
    </svg>
  `,
  className: "",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

// Utility to get locale from cookie
const getLocaleFromCookie = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

const EventMap = ({
  placeName,
  addressLine1,
  addressLine2,
  city,
  zipCode,
  country,
  latitude,
  longitude,
  url,
  startDate,
  endDate,
  vernissage,
  entranceCost,
}) => {
  const locale = getLocaleFromCookie();
  const { t } = useTranslation();

  // Sync i18n language with the cookie locale
  if (i18n.language !== locale) {
    i18n.changeLanguage(locale);
  }

  const getLocalizedCountry = (code) => {
    try {
      return new Intl.DisplayNames([locale], { type: "region" }).of(code);
    } catch {
      return code; // fallback
    }
  };

  const localizedCountry = country ? getLocalizedCountry(country) : "";

  if (!latitude || !longitude) return null;

  const position = [parseFloat(latitude), parseFloat(longitude)];

  return (
    <div className="container mt-5">
      <h2 className="mb-1 text-black">{t("events.address", "Address")}</h2>

      {/* Event Details List */}
      <ol className="list-group list-group-separated col-12 mb-4">
        {placeName && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.placeName", "Venue")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {placeName}
            </p>
          </li>
        )}

        {(addressLine1 || addressLine2) && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.address", "Address")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {addressLine1}
              {addressLine2 && ` - ${addressLine2}`}
            </p>
          </li>
        )}

        {city && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.city", "City")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {city}
            </p>
          </li>
        )}

        {zipCode && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.zipCode", "Zip")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {zipCode}
            </p>
          </li>
        )}

        {localizedCountry && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.country", "Country")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {localizedCountry}
            </p>
          </li>
        )}

        {url && (
          <li className="list-group-item d-flex justify-content-between align-items-start text-black">
            <span>
              <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
                {t("events.locationURL", "Url")}
              </p>
            </span>
            <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-black">
                {t("events.viewUrl", "Open")}
              </a>
            </p>
          </li>
        )}

        <li className="list-group-item d-flex justify-content-between align-items-start text-black">
          <span>
            <p className="lead mt-0 mb-0" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
              {t("events.getThere", "Get there")}
            </p>
          </span>
          <p className="lead mt-0 mb-0 text-end" style={{ lineHeight: "90%", textTransform: "uppercase" }}>
            <a
              href={`https://maps.google.com/?q=${latitude},${longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-black"
            >
              {t("events.getThereUrl", "Directions")}
            </a>
          </p>
        </li>
      </ol>

      {/* Map */}
      <div style={{ height: "400px" }}>
        <MapContainer
          center={position}
          zoom={18}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
          dragging={true}
          doubleClickZoom={false}
          attributionControl={false}
        >
          <TileLayer
            attribution=""
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <Marker position={position} icon={CustomMarker} />
        </MapContainer>
      </div>
    </div>
  );
};

export default EventMap;
